import { useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'

import { QUERY_KEYS } from '../../constants/constants'
import { useUserContext } from '../../context'
import { query } from '../../graphql'
import {
  parseCareerControlResult,
  parseSpaceControlResult,
  pointsDetail,
  revisionSummary,
  runRescorePages
} from '../../helpers/career-rescore'
import {
  BrandShapeBurst,
  BrandShapesLoader,
  CountUp,
  DURATION,
  EASE,
  GumTransfer,
  StepTransition,
  SuccessCheck,
  ValueFlip
} from '../motion'
import Button from '../tailwind/Button'
import Modal from '../tailwind/Modal'

// Nur für Aivy-Admins, deshalb ohne i18n wie Data und Logs in diesem Ordner.
// Ablauf: Vorschau (schreibt nichts) -> Bestätigen -> Stelle auf den aktuellen
// Regelstand heben -> alle abgeschlossenen Talente seitenweise nachrechnen.

const ROWS = [
  ['up', 'Score steigt'],
  ['down', 'Score sinkt'],
  ['unchanged', 'Score bleibt gleich'],
  ['unscored', 'Bisher ohne Score'],
  ['failed', 'Nicht berechenbar']
]

const fetchSpaceControlPage = (action, career_id) => async (cursor) => {
  const raw = await query({
    query: 'spaceControl',
    variables: { action, career_id, cursor }
  })
  return parseSpaceControlResult(raw, action)
}

// Zeilen gleiten nacheinander ein, die Zahlen zählen hoch
export const Counts = ({ summary, rows }) => (
  <dl className='mt-4 divide-y divide-gray-100 rounded-lg border border-gray-200'>
    {rows.map(([key, label], index) => (
      <motion.div
        key={key}
        className='flex items-center justify-between px-4 py-2 text-sm'
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{
          duration: DURATION.slow,
          ease: EASE.out,
          delay: 0.1 + index * 0.06
        }}
      >
        <dt className='text-gray-700'>
          {label}
          {pointsDetail(summary, key) && (
            <span className='block text-xs text-gray-500'>
              {pointsDetail(summary, key)}
            </span>
          )}
        </dt>
        <dd className='font-semibold tabular-nums text-gray-900'>
          <CountUp value={summary[key]} />
        </dd>
      </motion.div>
    ))}
  </dl>
)

Counts.propTypes = {
  summary: PropTypes.object.isRequired,
  rows: PropTypes.array.isRequired
}

const Revisions = ({ summary }) => (
  <dl className='mt-4 grid gap-3 rounded-lg bg-gray-50 p-4 text-sm'>
    {[
      ['Stelle rechnet heute mit', summary.current_revision, ''],
      ['Neu berechnet mit', summary.target_revision, ' (aktuell)']
    ].map(([label, revision, suffix]) => (
      <div key={label}>
        <dt className='text-gray-500'>{label}</dt>
        <dd className='font-semibold text-gray-900'>
          Regelstand {revision}
          {suffix}
        </dd>
        {revisionSummary(revision) && (
          <dd className='text-gray-600'>{revisionSummary(revision)}</dd>
        )}
      </div>
    ))}
  </dl>
)

Revisions.propTypes = {
  summary: PropTypes.object.isRequired
}

// Arbeitsanzeige: Aivy-Formen, Text und ein Balken, der mit den Seiten wächst.
// Bis die erste Seite total liefert, steht der Balken bei einem kleinen Anteil.
// Mit bekannten Regelständen wandern Score-Kreise vom alten zum neuen Stand,
// vorher hüpfen die Aivy-Formen.
export const Progress = ({ label, summary, fromRevision, toRevision }) => {
  const share =
    typeof summary.total === 'number' && summary.total > 0
      ? Math.min(1, summary.processed / summary.total)
      : 0.04
  const revisionsKnown =
    typeof fromRevision === 'number' && typeof toRevision === 'number'
  return (
    <div className='mt-6 flex flex-col items-center gap-4 pb-2'>
      {revisionsKnown ? (
        <GumTransfer
          fromLabel={`Regelstand ${fromRevision}`}
          toLabel={`Regelstand ${toRevision}`}
          progress={share}
        />
      ) : (
        <BrandShapesLoader className='mt-2' />
      )}
      <p className='text-sm text-gray-700'>
        {label}: {summary.processed}
        {typeof summary.total === 'number' ? ` von ${summary.total}` : ''}{' '}
        Talente
      </p>
      <div className='h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-paleBlue'>
        <motion.div
          className='h-full rounded-full bg-aivyBlue'
          initial={{ width: 0 }}
          animate={{ width: `${Math.max(share, 0.04) * 100}%` }}
          transition={{ duration: DURATION.slow, ease: EASE.out }}
        />
      </div>
    </div>
  )
}

Progress.propTypes = {
  label: PropTypes.string.isRequired,
  summary: PropTypes.object.isRequired,
  fromRevision: PropTypes.number,
  toRevision: PropTypes.number
}

Progress.defaultProps = {
  fromRevision: undefined,
  toRevision: undefined
}

// Abschluss: Haken mit Formen-Ausbruch, der Regelstand klappt vom alten auf
// den neuen Wert um. Ohne bekannten alten Wert (zweiter Lauf) steht nur der neue.
export const DoneHero = ({ previousRevision, targetRevision }) => (
  <div className='mt-6 flex flex-col items-center text-center'>
    <div className='relative flex size-28 items-center justify-center'>
      <BrandShapeBurst />
      <SuccessCheck className='size-14' />
    </div>
    <p className='mt-2 text-lg font-semibold text-gray-900'>
      Die Stelle nutzt jetzt die neueste Auswertung
    </p>
    <p className='mt-2 inline-flex items-center gap-1.5 rounded-full bg-paleWhiteBlue px-3 py-1 text-sm font-semibold text-aivyBlue'>
      Regelstand
      <ValueFlip
        from={
          previousRevision !== null && previousRevision !== targetRevision
            ? previousRevision
            : targetRevision
        }
        to={targetRevision}
      />
    </p>
    <p className='mt-3 max-w-sm text-sm text-gray-500'>
      Unsere Diagnostik wird laufend wissenschaftlich weiterentwickelt. Alle
      Talente dieser Stelle sind jetzt mit demselben, aktuellen Stand bewertet.
    </p>
  </div>
)

DoneHero.propTypes = {
  previousRevision: PropTypes.number,
  targetRevision: PropTypes.number.isRequired
}

DoneHero.defaultProps = {
  previousRevision: null
}

const CareerRescore = ({ open, setOpen, career }) => {
  const { isAdmin } = useUserContext()
  const queryClient = useQueryClient()

  // previewing | preview | up-to-date | applying | done | error
  const [phase, setPhase] = useState('previewing')
  const [summary, setSummary] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  // Nur nach einem Fehler beim Nachrechnen darf erneut ausgeführt werden. Nach
  // einem Fehler in der Vorschau wäre das ein Nachrechnen ohne Vorschau.
  const [failedWhileApplying, setFailedWhileApplying] = useState(false)
  // Regelstand vor dem Heben, für das Umklappen im Fertig-Schritt
  const [previousRevision, setPreviousRevision] = useState(null)
  const [targetRevision, setTargetRevision] = useState(null)
  const cancelled = useRef(false)

  const startPreview = async () => {
    cancelled.current = false
    setPhase('previewing')
    setSummary(null)
    setErrorMessage(null)
    try {
      const result = await runRescorePages({
        fetchPage: fetchSpaceControlPage('previewCareerRescore', career.id),
        onProgress: setSummary,
        isCancelled: () => cancelled.current
      })
      if (result.cancelled) return
      setSummary(result.summary)
      setPhase(
        result.summary.current_revision === result.summary.target_revision
          ? 'up-to-date'
          : 'preview'
      )
    } catch (err) {
      console.error(err)
      setErrorMessage(err.message || String(err))
      setFailedWhileApplying(false)
      setPhase('error')
    }
  }

  const apply = async () => {
    // Regelstände aus der Vorschau, bis careerControl sie bestätigt
    setPreviousRevision(summary?.current_revision ?? null)
    setTargetRevision(summary?.target_revision ?? null)
    setPhase('applying')
    setSummary(null)
    setErrorMessage(null)
    try {
      const raw = await query({
        query: 'careerControl',
        variables: {
          action: 'setCareerScoringRevisionToCurrent',
          career_id: career.id
        }
      })
      const raised = parseCareerControlResult(
        raw,
        'setCareerScoringRevisionToCurrent'
      )
      setPreviousRevision(raised.previous_revision)
      setTargetRevision(raised.scoring_revision)
      // Ohne Abbruch: Ein halber Lauf ließe die Stelle gemischt zurück
      const result = await runRescorePages({
        fetchPage: fetchSpaceControlPage('rescoreCareerSpaces', career.id),
        onProgress: setSummary
      })
      setSummary(result.summary)
      setPhase('done')
    } catch (err) {
      console.error(err)
      setErrorMessage(err.message || String(err))
      setFailedWhileApplying(true)
      setPhase('error')
    } finally {
      queryClient.invalidateQueries({ queryKey: ['talents'] })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.careerById(career.id)
      })
    }
  }

  useEffect(() => {
    if (open && isAdmin) startPreview()
    return () => {
      cancelled.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, career.id])

  if (!isAdmin) return null

  const busy = phase === 'applying'

  return (
    <Modal
      open={open}
      setOpen={busy ? () => {} : setOpen}
      size='lg'
      hideCloseButton={busy}
      disableOverlayClose={busy}
    >
      <h2 className='text-2xl font-extrabold tracking-tight text-gray-900'>
        Stelle neu berechnen
      </h2>
      <p className='mt-2 text-sm text-gray-500'>{career.title}</p>

      <StepTransition stepKey={phase}>
        {phase === 'previewing' && (
          <Progress
            label='Vorschau läuft'
            summary={summary || { processed: 0 }}
            fromRevision={summary?.current_revision}
            toRevision={summary?.target_revision}
          />
        )}

        {phase === 'up-to-date' && (
          <>
            <p className='mt-4 text-sm text-gray-700'>
              Die Stelle rechnet schon mit dem aktuellen Regelstand{' '}
              {summary.target_revision}.
            </p>
            <p className='mt-2 text-sm text-gray-500'>
              Ist ein früherer Lauf abgebrochen, rechnet „Fehlende nachrechnen“
              nur die Talente nach, die noch einen älteren Stand haben.
            </p>
            <div className='mt-6 flex justify-end gap-x-3'>
              <Button.SecondaryLG text='Fehlende nachrechnen' onClick={apply} />
              <Button.PrimaryLG
                text='Schließen'
                onClick={() => setOpen(false)}
              />
            </div>
          </>
        )}

        {phase === 'preview' && (
          <>
            <Revisions summary={summary} />
            <p className='mt-4 text-sm text-gray-700'>
              So ändern sich die {summary.total} abgeschlossenen Talente:
            </p>
            <Counts summary={summary} rows={ROWS} />
            <p className='mt-4 text-sm text-gray-500'>
              Alle Talente der Stelle werden gemeinsam nachgerechnet. Scores,
              die schon an ein ATS übertragen wurden, bleiben dort unverändert.
              Die alten Werte stehen je Talent im Log.
            </p>
            <div className='mt-6 flex justify-end gap-x-3'>
              <Button.SecondaryLG
                text='Abbrechen'
                onClick={() => setOpen(false)}
              />
              <Button.PrimaryLG text='Jetzt neu berechnen' onClick={apply} />
            </div>
          </>
        )}

        {phase === 'applying' && (
          <Progress
            label='Berechne neu'
            summary={summary || { processed: 0 }}
            fromRevision={previousRevision ?? undefined}
            toRevision={targetRevision ?? undefined}
          />
        )}

        {phase === 'done' && (
          <>
            <DoneHero
              previousRevision={previousRevision}
              targetRevision={summary.target_revision}
            />
            <Counts
              summary={summary}
              rows={[...ROWS, ['skipped', 'Schon nachgerechnet']]}
            />
            {summary.failed > 0 && (
              <p className='mt-4 text-sm text-red-600'>
                {summary.failed} Talente ließen sich nicht berechnen. Ein
                erneuter Lauf rechnet nur die noch fehlenden nach.
              </p>
            )}
            <div className='mt-6 flex justify-end gap-x-3'>
              {summary.failed > 0 && (
                <Button.SecondaryLG text='Erneut ausführen' onClick={apply} />
              )}
              <Button.PrimaryLG
                text='Schließen'
                onClick={() => setOpen(false)}
              />
            </div>
          </>
        )}

        {phase === 'error' && (
          <>
            <p className='mt-4 text-sm text-red-600'>
              Abgebrochen: {errorMessage}
            </p>
            <p className='mt-2 text-sm text-gray-500'>
              Wurde schon nachgerechnet, setzt ein erneuter Lauf dort fort, wo
              er stehen geblieben ist.
            </p>
            <div className='mt-6 flex justify-end gap-x-3'>
              <Button.SecondaryLG
                text='Schließen'
                onClick={() => setOpen(false)}
              />
              <Button.SecondaryLG
                text='Vorschau neu laden'
                onClick={startPreview}
              />
              {failedWhileApplying && (
                <Button.PrimaryLG text='Erneut ausführen' onClick={apply} />
              )}
            </div>
          </>
        )}
      </StepTransition>
    </Modal>
  )
}

CareerRescore.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  career: PropTypes.object.isRequired
}

export default CareerRescore
